import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SeoHead — Reusable SEO head injector.
 *
 * Centralizes title / description / canonical / Open Graph / Twitter card and
 * optional JSON-LD structured data. Pass only what differs from the defaults.
 *
 * Default site identity: Pharmacie Saint Ange Des Amis (Angré 7ème Tranche,
 * Cocody, Abidjan). Keywords and descriptions are calibrated for local SEO
 * (pharmacie Cocody, pharmacie Angré, pharmacie de référence, etc.).
 */

const SITE_URL = 'https://phciesada.org';
const DEFAULT_TITLE =
  "Pharmacie Saint Ange Des Amis — Pharmacie de référence à Cocody, Angré 7ème Tranche";
const DEFAULT_DESCRIPTION =
  "Pharmacie Saint Ange Des Amis à Angré 7ème Tranche (Cocody, Abidjan). Hall aéré et spacieux, équipe jeune et dynamique, accueil chaleureux, commandes France & Suisse, laits infantiles rares, produits FIV, parapharmacie fournie, conseil dermo-cosmétique via IA et suivi santé avec rapport imprimable.";
const DEFAULT_KEYWORDS = [
  'pharmacie Cocody',
  'pharmacie Angré',
  'pharmacie 7ème tranche',
  'pharmacie de référence',
  'pharmacie bon accueil',
  'pharmacie commande France',
  'produits rares Abidjan',
  'grande pharmacie Abidjan',
  'meilleure pharmacie Cocody',
  'pharmacie bien achalandée',
  'parapharmacie Angré',
  'laits infantiles rares Abidjan',
  'produits FIV Abidjan',
  'pharmacie de garde Cocody',
  'Pharmacie Saint Ange Des Amis',
].join(', ');
const DEFAULT_IMAGE = `${SITE_URL}/logo-pharmacie.jpg`;

const SeoHead = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonical,
  image = DEFAULT_IMAGE,
  type = 'website',
  structuredData,
  noIndex = false,
}) => {
  const fullTitle = title
    ? `${title} | Pharmacie Saint Ange Des Amis`
    : DEFAULT_TITLE;
  const fullCanonical = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullCanonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Pharmacie Saint Ange Des Amis" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
